import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  CancelButton,
  PrimaryButtonWithPlusIcon,
} from "../../../../components/Buttons/Buttons";
import { HeaderLeft, HeaderRight } from "../../../../components/Layout/Layout";
import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import {
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../layout/portalPageLayout";
import type {
  CreateAlertPayload,
  GetSystemMessagesResponse,
} from "../../../../types/types";
import { useRoutePath } from "../../../../util/Routing";
import { CreateBroadcastForm } from "./CreateBroadcastForm";
import { useStoreState } from "../../../../util/util";
import axios from "axios";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import useSWR from "swr";
import styled from "styled-components/macro";
import { Loader } from "../../../../components/Loader/Loader";
import { ErrorPlaceholder } from "../../../../components/Error";
import { BroadcastCard } from "./BroadcastCard";
import { useAuthContext } from "../../../../components/Auth";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { Pagination } from "../../../../components/Pagination/Pagination";

const SubtitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  margin-top: 1.5rem;
  padding: 1rem 0;
`;

const SubtitleHeader = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  margin: 0;
`;

const SubtitleDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.secondaryTextColor};
  margin: 0.25rem 0 0 0;
`;

const BroadcastList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  /* Remove border radius from cards when stacked in admin view */
  > div:not(:first-child) {
    border-radius: 0;
    margin-top: -1px; /* Prevent border stacking */
  }
  > div:last-child {
    border-radius: 0 0 ${({ theme }) => theme.resolve("radius_xs")}
      ${({ theme }) => theme.resolve("radius_xs")};
  }

  margin-bottom: 2rem;
`;

const BroadcastListHeader = styled.div`
  height: 54px;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: 15px ${({ theme }) => theme.resolve("spacing_xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")}
    ${({ theme }) => theme.resolve("radius_xs")} 0 0;
  background-color: ${({ theme }) => theme.secondaryBG};
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: ${({ theme }) => theme.fontWeights.large};
  color: ${({ theme }) => theme.primaryTextColor};
`;

const LIMIT = 10;

export const BroadcastsPage = () => {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const [isSlideOutOpen, setIsSlideOutOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [messageToArchiveId, setMessageToArchiveId] =
    useState<string | null>(null);
  const [activeOffset, setActiveOffset] = useState(0);
  const [inactiveOffset, setInactiveOffset] = useState(0);
  const { storefront_id } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { roleIsSellerAdmin } = useAuthContext();

  const messagesUrl = `/v2/storefronts/${storefront_id}/system-messages`;
  const activeMessagesUrl = `${messagesUrl}?state=published&limit=${LIMIT}&offset=${activeOffset}`;
  const inactiveMessagesUrl = `${messagesUrl}?state=archived&limit=${LIMIT}&offset=${inactiveOffset}`;

  const {
    data: activeMessagesResponse,
    error: activeMessagesError,
    mutate: mutateActiveMessages,
  } = useSWR<GetSystemMessagesResponse>(activeMessagesUrl);

  const {
    data: inactiveMessagesResponse,
    error: inactiveMessagesError,
    mutate: mutateInactiveMessages,
  } = useSWR<GetSystemMessagesResponse>(inactiveMessagesUrl);

  const activeMessages = activeMessagesResponse?.data ?? [];
  const inactiveMessages = inactiveMessagesResponse?.data ?? [];

  const isLoading =
    !activeMessagesResponse &&
    !inactiveMessagesResponse &&
    !activeMessagesError &&
    !inactiveMessagesError;
  const hasError = !!activeMessagesError || !!inactiveMessagesError;

  const activeTotal = activeMessagesResponse?.pagination?.total ?? 0;
  const activePageCount = Math.ceil(activeTotal / LIMIT);
  const activePageIndex = Math.floor(activeOffset / LIMIT) + 1;

  const inactiveTotal = inactiveMessagesResponse?.pagination?.total ?? 0;
  const inactivePageCount = Math.ceil(inactiveTotal / LIMIT);
  const inactivePageIndex = Math.floor(inactiveOffset / LIMIT) + 1;

  const handleOpenCreateSlideOut = () => {
    setIsSlideOutOpen(true);
  };

  const handleCloseCreateSlideOut = () => {
    setIsSlideOutOpen(false);
  };

  const handleActivePageClick = (newOffset: number) => {
    setActiveOffset(newOffset);
  };

  const handleInactivePageClick = (newOffset: number) => {
    setInactiveOffset(newOffset);
  };

  const handleCancelButtonClick = () => {
    history.push(`${adminPath}/dashboard`);
  };

  const handleSubmit = async (data: CreateAlertPayload) => {
    setIsSubmitting(true);
    const url = `/v2/storefronts/${storefront_id}/system-messages`;
    const method = "post";
    const successMessage = t("Broadcast created successfully");
    const errorMessage = t("Failed to create broadcast. Please try again.");

    try {
      await axios({ method, url, data });
      notifySuccess(successMessage);
      handleCloseCreateSlideOut();
      await Promise.all([mutateActiveMessages(), mutateInactiveMessages()]);
    } catch (error) {
      console.error(`Failed to create broadcast:`, error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response?.data?.message
          : errorMessage;
      notifyError(message, { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveClick = (messageId: string) => {
    setMessageToArchiveId(messageId);
    setShowArchiveConfirm(true);
  };

  const cancelArchiveHandler = () => {
    setShowArchiveConfirm(false);
    setMessageToArchiveId(null);
  };

  const confirmArchiveHandler = async () => {
    if (!messageToArchiveId) return;

    const url = `/v2/storefronts/${storefront_id}/system-messages/${messageToArchiveId}`;
    try {
      await axios.delete(url);
      notifySuccess(t("Broadcast archived"));
      await Promise.all([mutateActiveMessages(), mutateInactiveMessages()]);
    } catch (error) {
      console.error("Failed to archive broadcast:", error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response?.data?.message
          : t("Failed to archive broadcast. Please try again.");
      notifyError(message, { error });
    } finally {
      cancelArchiveHandler();
    }
  };

  return (
    <PageWrapper>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>{t("Broadcasts")}</PageTitle>
        </HeaderLeft>
        <HeaderRight>
          <CancelButton onClick={handleCancelButtonClick}>
            {t("Cancel")}
          </CancelButton>
        </HeaderRight>
      </PageHeader>
      <FullWidthHorizontalSeparator />

      <SubtitleSection>
        <div>
          <SubtitleHeader>{t("Manage User Broadcasts")}</SubtitleHeader>
          <SubtitleDescription>
            {t(
              "Create, view, and manage messages displayed to all logged in users."
            )}
          </SubtitleDescription>
        </div>
        <PrimaryButtonWithPlusIcon onClick={handleOpenCreateSlideOut}>
          {t("Create")}
        </PrimaryButtonWithPlusIcon>
      </SubtitleSection>

      <ContentWrapper>
        {hasError ? (
          <ErrorPlaceholder message={t("Failed to load broadcasts.")} />
        ) : isLoading ? (
          <Loader isLoading={isLoading} />
        ) : (
          <>
            <BroadcastList>
              {activeMessages.length > 0 ? (
                <>
                  <BroadcastListHeader>{t("Active")}</BroadcastListHeader>
                  {activeMessages.map((message) => (
                    <BroadcastCard
                      key={message.id}
                      message={message}
                      onArchive={() => handleArchiveClick(message.id)}
                      canArchive={roleIsSellerAdmin}
                      showActions={roleIsSellerAdmin}
                      isAdminView={true}
                    />
                  ))}
                  {activeTotal > LIMIT && (
                    <Pagination
                      pagination={{
                        pageCount: activePageCount,
                        pageIndex: activePageIndex,
                        perPage: LIMIT,
                      }}
                      offset={activeOffset}
                      handlePageClick={handleActivePageClick}
                    />
                  )}
                </>
              ) : (
                <></>
              )}
            </BroadcastList>

            <BroadcastList>
              {inactiveMessages.length > 0 ? (
                <>
                  <BroadcastListHeader>{t("Inactive")}</BroadcastListHeader>
                  {inactiveMessages.map((message) => (
                    <BroadcastCard
                      key={message.id}
                      message={message}
                      onArchive={() => handleArchiveClick(message.id)}
                      canArchive={false}
                      showActions={roleIsSellerAdmin}
                      isAdminView={true}
                    />
                  ))}
                  {inactiveTotal > LIMIT && (
                    <Pagination
                      pagination={{
                        pageCount: inactivePageCount,
                        pageIndex: inactivePageIndex,
                        perPage: LIMIT,
                      }}
                      offset={inactiveOffset}
                      handlePageClick={handleInactivePageClick}
                    />
                  )}
                </>
              ) : (
                <></>
              )}
            </BroadcastList>
          </>
        )}
      </ContentWrapper>

      <SlideOut
        title={t("Create Broadcast Message")}
        show={isSlideOutOpen}
        closeFlyout={handleCloseCreateSlideOut}
      >
        <CreateBroadcastForm
          key={"create"}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </SlideOut>

      <ConfirmDialog
        show={showArchiveConfirm}
        closeDialog={cancelArchiveHandler}
        handleConfirm={confirmArchiveHandler}
        handleCancel={cancelArchiveHandler}
        confirmMessage={t("Are you sure you want to archive this broadcast?")}
      />
    </PageWrapper>
  );
};
